import { useRef, useState } from "react";
import { Plus, Search, SlidersHorizontal } from "lucide-react";
import { FilterPills, type FilterPill } from "./FilterPills";
import {
  FilterPopover,
  type FilterOptionGroup,
} from "./FilterPopover";
import { SortDropdown, SORT_OPTIONS, type SortDir, type SortKey } from "./SortDropdown";
import { ViewToggle, type ViewMode } from "./ViewToggle";

type Props = {
  query: string;
  onQueryChange: (v: string) => void;
  filterGroups: FilterOptionGroup[];
  activeFilters: Record<string, string[]>;
  onFiltersChange: (next: Record<string, string[]>) => void;
  sortKey?: SortKey;
  onSortChange?: (v: SortKey) => void;
  sortDir?: SortDir;
  onSortDirChange?: (d: SortDir) => void;
  sortOptions?: typeof SORT_OPTIONS;
  viewMode?: ViewMode;
  onViewModeChange?: (v: ViewMode) => void;
  /** When provided, renders an "Add new" CTA next to the view toggle. */
  onAddNew?: () => void;
  addLabel?: string;
  /** Quick-filter slicer options rendered inline after the Filter button. */
  slicerOptions?: string[];
  selectedSlicerValues?: string[];
  onSlicerToggle?: (value: string) => void;
};

export function FiltersBar({
  query,
  onQueryChange,
  filterGroups,
  activeFilters,
  onFiltersChange,
  sortKey,
  onSortChange,
  sortDir,
  onSortDirChange,
  sortOptions,
  viewMode,
  onViewModeChange,
  onAddNew,
  addLabel = "Add new",
  slicerOptions,
  selectedSlicerValues = [],
  onSlicerToggle,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  // Expand active filters into one pill per selected value so each can be
  // removed individually without nuking the whole group.
  const pills: FilterPill[] = Object.entries(activeFilters).flatMap(
    ([key, values]) => {
      const group = filterGroups.find((g) => g.key === key);
      const label = group?.label ?? key;
      return (values ?? []).map((value) => ({ key, label, value }));
    }
  );

  const removeFilterValue = (key: string, value: string) => {
    const current = activeFilters[key] ?? [];
    const remaining = current.filter((v) => v !== value);
    const next = { ...activeFilters };
    if (remaining.length === 0) delete next[key];
    else next[key] = remaining;
    onFiltersChange(next);
  };

  const activeCount = pills.length;

  return (
    <div className="filters-wrapper">
      <div className="filters-row">
        <div className="filter-search">
          <Search
            size={16}
            className="filter-search-icon"
            aria-hidden
          />
          <input
            className="filter-search-input"
            type="search"
            placeholder="Search…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>
        <button
          ref={triggerRef}
          type="button"
          className={`filter-trigger${activeCount > 0 ? " has-filters" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <SlidersHorizontal size={16} />
          <span>Filter</span>
          {activeCount > 0 ? (
            <span className="filter-trigger-count">{activeCount}</span>
          ) : null}
        </button>
        {slicerOptions && slicerOptions.length > 0 && onSlicerToggle ? (
          <div className="slicer-inline">
            {slicerOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`slicer-btn${selectedSlicerValues.includes(opt) ? " is-active" : ""}`}
                onClick={() => onSlicerToggle(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        ) : null}
        <div className="filters-row-spacer" />
        {sortKey && onSortChange && sortDir && onSortDirChange ? (
          <SortDropdown
            value={sortKey}
            onChange={onSortChange}
            direction={sortDir}
            onDirectionChange={onSortDirChange}
            options={sortOptions}
          />
        ) : null}
        {viewMode && onViewModeChange ? (
          <ViewToggle value={viewMode} onChange={onViewModeChange} />
        ) : null}
        {onAddNew ? (
          <button
            type="button"
            className="filter-add-cta"
            onClick={onAddNew}
            aria-label={addLabel}
          >
            <span className="filter-add-cta-icon" aria-hidden>
              <Plus size={18} strokeWidth={2.75} />
            </span>
            <span className="filter-add-cta-label">{addLabel}</span>
          </button>
        ) : null}
      </div>
      {pills.length > 0 ? (
        <FilterPills
          pills={pills}
          onRemove={removeFilterValue}
          onClearAll={() => onFiltersChange({})}
        />
      ) : null}
      <FilterPopover
        open={open}
        anchor={triggerRef.current}
        groups={filterGroups}
        initialValues={activeFilters}
        onApply={onFiltersChange}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
